import { useEffect, useMemo, useState } from 'react';

import type { InvertedLocalize } from '../../../shared/hooks/use-invertedlocalize';
import type {
  LaunchDetail,
  RequestMetadata,
} from '../data-layer/auditLogTypes';
import { formatDateByGlobal, formatDurationHms } from '../utils/utils';

export interface RecordingSessionViewModel {
  id: string;
  title: string;
  dateTime: string;
  duration: string;
  launchStartDate: string;
  launchEndDate: string;
  sessionType: 'windows' | 'terminal';
  previewSrc: string;
  isLive: boolean;
  launchType: string | null;
}

// Need to revisit this logic to support terminal playback again
function deriveSessionType(
  requestMetadata: RequestMetadata | null
): 'windows' | 'terminal' {
  const connection = (requestMetadata?.connectiontype ?? '').toLowerCase();
  const application = (requestMetadata?.applicationType ?? '').toLowerCase();

  if (
    connection.includes('ssh') ||
    connection.includes('linux') ||
    connection.includes('unix') ||
    application.includes('sapconsole') ||
    application.includes('terminal')
  ) {
    return 'terminal';
  }

  return 'windows';
}

/**
 * Returns a memoized list of normalized recording sessions derived from
 * the raw `launchDetail` payload. It enriches each session with formatted
 * titles, timestamps, durations, and the resolved session type so the UI
 * can render cards/playlists without duplicating this logic.
 */
export function useRecordingSessionsViewModel(params: {
  launchDetail: LaunchDetail[] | undefined;
  requestMetadata: RequestMetadata | null;
  globalDateFormat?: string;
  localize: InvertedLocalize<string>;
  excludedLaunchIds?: Set<string>;
}): RecordingSessionViewModel[] {
  const {
    launchDetail,
    requestMetadata,
    globalDateFormat,
    localize: l,
    excludedLaunchIds,
  } = params;

  return useMemo(() => {
    if (!Array.isArray(launchDetail) || launchDetail.length === 0) {
      return [];
    }

    const sessionType = deriveSessionType(requestMetadata);

    // Terminal playback is no longer supported; skip unsupported sessions early
    if (sessionType === 'terminal') {
      return [];
    }

    const completedLaunches = launchDetail
      .filter((session) => Boolean(session.launchEndDate))
      .filter((session) => {
        const id = String(session.launchId);

        return !excludedLaunchIds?.has(id);
      });

    return completedLaunches.map((session, index) => {
      const total = completedLaunches.length || 1;

      return {
        id: String(session.launchId),
        title: l('Session {index} of {total}', {
          index: index + 1,
          total,
        }),
        dateTime: formatDateByGlobal(session.launchStartDate, globalDateFormat),
        duration: formatDurationHms(
          session.launchStartDate,
          session.launchEndDate
        ),
        launchStartDate: session.launchStartDate,
        launchEndDate: session.launchEndDate,
        sessionType,
        previewSrc: '',
        // Only completed sessions are included in this list.
        isLive: false,
        launchType: requestMetadata?.applicationType ?? null,
      } satisfies RecordingSessionViewModel;
    });
  }, [launchDetail, requestMetadata, globalDateFormat, l, excludedLaunchIds]);
}

interface UseRecordingSelectionResult {
  recordedSessions: RecordingSessionViewModel[];
  recordingSelectedIndex: number;
  setRecordingSelectedIndex: (index: number) => void;
  isRecordingPlayerView: boolean;
  setIsRecordingPlayerView: (value: boolean) => void;
  hasRecordedSessions: boolean;
  selectedRecordingSession: RecordingSessionViewModel | undefined;
}

/**
 * Tracks the currently selected recording and whether the playlist or
 * player view is active. It automatically keeps the selection in sync with
 * the available sessions when the data set changes.
 */
export function useRecordingSelection(
  recordedSessions: RecordingSessionViewModel[]
): UseRecordingSelectionResult {
  const [recordingSelectedIndex, setRecordingSelectedIndex] = useState(0);
  const [isRecordingPlayerView, setIsRecordingPlayerView] = useState(false);

  useEffect(() => {
    if (
      recordedSessions.length > 0 &&
      recordingSelectedIndex >= recordedSessions.length
    ) {
      setRecordingSelectedIndex(0);
    }
  }, [recordedSessions.length, recordingSelectedIndex]);

  useEffect(() => {
    if (recordedSessions.length === 0 && isRecordingPlayerView) {
      setIsRecordingPlayerView(false);
    }
  }, [recordedSessions.length, isRecordingPlayerView]);

  const selectedRecordingSession: RecordingSessionViewModel | undefined =
    recordedSessions.length > 0
      ? recordedSessions[recordingSelectedIndex] ?? recordedSessions[0]
      : undefined;

  return {
    recordedSessions,
    recordingSelectedIndex,
    setRecordingSelectedIndex,
    isRecordingPlayerView,
    setIsRecordingPlayerView,
    hasRecordedSessions: recordedSessions.length > 0,
    selectedRecordingSession,
  };
}

/**
 * Returns true if URL is safe for script/stylesheet loading (CWE-601, CWE-829).
 * Allows only relative paths (/) or same-origin absolute URLs.
 */
function isAllowedResourceUrl(url: string): boolean {
  const trimmed = url.trim();

  if (trimmed.length === 0) {
    return false;
  }

  const lower = trimmed.toLowerCase();

  // Reject script/data URLs (CWE-829); build protocol strings to avoid no-script-url
  const jsProtocol = `${'javascript'}${':'}`;
  const dataProtocol = `${'data'}${':'}`;
  const vbsProtocol = `${'vbscript'}${':'}`;

  if (
    lower.startsWith(jsProtocol) ||
    lower.startsWith(dataProtocol) ||
    lower.startsWith(vbsProtocol)
  ) {
    return false;
  }

  if (trimmed.startsWith('/')) {
    return true;
  }

  try {
    const parsed = new URL(trimmed, window.location.origin);

    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

// Shared asset loaders (idempotent by element id)
export const loadCss = (href: string, id: string) =>
  new Promise<void>((resolve, reject) => {
    if (!isAllowedResourceUrl(href)) {
      reject(new Error(`Disallowed CSS URL: ${href}`));

      return;
    }

    if (document.getElementById(id)) {
      resolve();

      return;
    }

    const link = document.createElement('link');

    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));

    document.head.appendChild(link);
  });

export const loadScript = (src: string, id: string) =>
  new Promise<void>((resolve, reject) => {
    if (!isAllowedResourceUrl(src)) {
      reject(new Error(`Disallowed script URL: ${src}`));

      return;
    }

    if (document.getElementById(id)) {
      resolve();

      return;
    }

    const script = document.createElement('script');

    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

    document.body.appendChild(script);
  });

let preloadPromise: Promise<void> | null = null;

/**
 * Preload player assets (CSS + JS) ahead of opening the recording panel.
 * Safe to call multiple times; subsequent calls reuse the same promise.
 * Assets are loaded in parallel for faster initialization.
 */
export const preloadSessionRecordingAssets = (): Promise<void> => {
  if (preloadPromise) {
    return preloadPromise;
  }

  // Load all assets in parallel for faster initialization
  preloadPromise = Promise.all([
    loadCss('/ECMv6/api/rdpgateway/playbackwithevents.css', 'aag-playback-css'),
    loadScript(
      '/ECMv6/api/rdpgateway/guacamole-common-js/all.js',
      'aag-guac-lib'
    ),
    loadScript('/ECMv6/api/rdpgateway/playback.js', 'aag-playback-lib'),
  ])
    .then(() => {
      // Return void to match return type
    })
    .catch((err) => {
      // Allow future calls to retry if any asset fails to load
      preloadPromise = null;
      throw err;
    });

  return preloadPromise;
};
